import React from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import { FormatPrice } from 'utils/format';

import { withFormikField, getFormikError } from 'utils/formik';

import {
  StyledSelect,
  StyledExtraContainer,
  StyledFirstColumn,
  StyledFieldWrapper,
  StyledHelpText,
} from './styled';

import { Spacer, Flexer, Checkbox, Textarea, Field, Feedback } from '@ontruck/orc';

import {
  EXTRA_NUMERIC,
  EXTRA_PERCENT,
  includedInBaseList,
} from 'modules/shipper-order/Form/constants';

import { ExtraApi } from 'modules/shipper-order/Form/utils';

const t = {
  shipper_order: 'S',
  shipper_visible: 'Details (will be displayed to the shipper)',
  shipper_not_visible: "Details (won't be displayed to the shipper)",
  contract_alert:
    'Watch out! This SO is part of an existing contract, please double check if the price is correct before modifying it.',
};

const ExtraElement = ({
  intl,
  label,
  showExtraComment,
  extraField,
  extrasAmountsField,
  extraName,
  extraType,
  isVisibleByShipper,
  isNegative,
  includedInBaseList,
  currency,
  showAmount,
  amountFieldContainerWidth,
  form,
  disabled,
  extraValues,
  showReferral,
  referral,
  quote,
  disableField,
  inputHelp,
  children,
  showAlert,
  isLast,
}) => {
  const currencySymbol = intl.messages.currencies[currency];

  const { values, setFieldValue } = form;

  const extraApi = new ExtraApi({
    fieldName: extrasAmountsField,
    setFieldValue,
    values,
    extraName,
    extraType,
    isNegative,
    referral,
  });

  const quoteAmountsValues = quote[extrasAmountsField] || [];
  const extrasAmountsValues = extraApi.getAmounts();
  const errorPosition = extrasAmountsValues.map(({ concept }) => concept).indexOf(extraName);
  const error = Object.keys(
    getFormikError({ field: { name: `${extrasAmountsField}.${errorPosition}` }, form }) || {}
  ).length;
  const extra = extraApi.getExtra();
  const quoteExtra = quoteAmountsValues.find(extraApi.findExtra) || {};
  const isChecked = !!Object.keys(extra).length;
  const { amount, description, referral_shipper_order_id } = extra;

  const { net } = quoteExtra;

  const extraAmount = extraApi.sanitizeExtra(amount);
  const extraNetValue = net;

  const isPercentage = extraType === EXTRA_PERCENT;

  const ExtraField =
    extraValues && extraValues.length ? (
      <StyledSelect
        ignoreCase={true}
        matchPos="any"
        searchable={false}
        value={parseInt(extraAmount, 10)}
        onChange={({ value }) => {
          extraApi.changeExtraValue(null, { value });
        }}
        options={extraValues.map((value) => ({
          value,
          label: `${value} ${isPercentage ? '%' : currencySymbol}`,
        }))}
        clearable={false}
        small
      />
    ) : (
      <Field
        value={disableField ? '' : extraAmount || ''}
        labelType="hidden"
        width="S"
        asideLeftText={isNegative ? '–' : null}
        asideRightText={isPercentage ? '%' : currencySymbol}
        onChange={extraApi.changeExtraValue}
        invalid={!!error}
        disabled={disableField}
        dataE2E={`detail_extras__${extraName}_field`}
      />
    );

  const referralLabel =
    (referral_shipper_order_id &&
      showReferral &&
      ` ${t.shipper_order} ${referral_shipper_order_id}`) ||
    '';

  return (
    <StyledExtraContainer isLast={isLast}>
      <Flexer.Parent alignItems="center">
        <StyledFirstColumn p="S">
          <Spacer>
            <Checkbox
              checked={isChecked}
              label={`${label}${referralLabel}`}
              onChange={extraApi.toggleExtra}
              disabled={disabled}
              dataE2E={`detail_extras__${extraName}_checkbox`}
            />
          </Spacer>
        </StyledFirstColumn>
        <StyledFieldWrapper pTop="XS" pBottom="XS" visible={isChecked}>
          <Flexer.Parent alignItems="center">
            <Flexer.Child
              {...((amountFieldContainerWidth && { style: { width: amountFieldContainerWidth } }) ||
                {})}
            >
              {ExtraField}
            </Flexer.Child>
            {(isPercentage || showAmount) && (
              <Flexer.Child {...((!amountFieldContainerWidth && { mLeft: 'S' }) || {})}>
                <StyledHelpText>
                  <FormatPrice currency={currency} value={extraNetValue} />
                </StyledHelpText>
              </Flexer.Child>
            )}
            <Flexer.Child mLeft="S" mRight="S">
              {showAlert && (
                <Feedback size="wideS" skin="error">
                  {t.contract_alert}
                </Feedback>
              )}
              {!showAlert && <StyledHelpText>{inputHelp} </StyledHelpText>}
            </Flexer.Child>
          </Flexer.Parent>
        </StyledFieldWrapper>
        {!!isChecked && children}
      </Flexer.Parent>
      {isChecked && showExtraComment && (
        <Spacer p="S" pTop="N">
          <Textarea
            value={description || ''}
            labelType="hidden"
            placeholder={(isVisibleByShipper && t.shipper_visible) || t.shipper_not_visible}
            height={'100px'}
            width="L"
            onChange={extraApi.handleDescriptionChange}
          />
        </Spacer>
      )}
    </StyledExtraContainer>
  );
};

ExtraElement.propTypes = {
  showExtraComment: PropTypes.bool,
  label: PropTypes.string.isRequired,
  extraName: PropTypes.string.isRequired,
  extraField: PropTypes.string,
  extraType: PropTypes.oneOf([EXTRA_NUMERIC, EXTRA_PERCENT]),
  isVisibleByShipper: PropTypes.bool,
  includedInBaseList: PropTypes.array,
  currency: PropTypes.string.isRequired,
  showAmount: PropTypes.bool,
  amountFieldContainerWidth: PropTypes.number,
  intl: PropTypes.object.isRequired,
  extrasAmountsField: PropTypes.object.isRequired,
  isNegative: PropTypes.bool,
  form: PropTypes.object,
  extraValues: PropTypes.array,
  showReferral: PropTypes.bool,
  disabled: PropTypes.bool,
  referral: PropTypes.number,
  quote: PropTypes.object,
  disableField: PropTypes.bool,
  inputHelp: PropTypes.string,
};

ExtraElement.defaultProps = {
  showExtraComment: false,
  extraField: 'extras',
  extrasAmountsField: 'extra_prices',
  extraType: EXTRA_NUMERIC,
  isVisibleByShipper: true,
  includedInBaseList: includedInBaseList,
  showAmount: false,
  amountFieldContainerWidth: false,
  isNegative: false,
  extraValues: [],
  showReferral: true,
  disabled: false,
  quote: {},
  disableField: false,
  inputHelp: '',
};

export default injectIntl(withFormikField(ExtraElement));
